import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utils/axiosInstance";
import type { UnifiedTicket } from "./helpdeskTypes";

function macIdsToString(metadata: Record<string, unknown> | null | undefined): string {
  if (!metadata) return "";
  const m = metadata.machineMacIds;
  if (Array.isArray(m)) return m.map(String).join(", ");
  if (typeof m === "string") return m;
  return "";
}

function parseMacIds(raw: string): string[] {
  return raw
    .split(/[,;\n]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

type Props = {
  open: boolean;
  ticket: UnifiedTicket | null;
  onClose: () => void;
  onSaved: () => void;
  onOpenCustomer: (customerId: string) => void;
};

export default function HelpdeskTicketDetailDialog({
  open,
  ticket,
  onClose,
  onSaved,
  onOpenCustomer,
}: Props) {
  const [macInput, setMacInput] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [privateNote, setPrivateNote] = useState(false);
  const [zammadArticleType, setZammadArticleType] = useState<"note" | "email">("note");
  const [metaLoading, setMetaLoading] = useState(false);
  const [replyLoading, setReplyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !ticket) return;
    setMacInput(macIdsToString(ticket.metadata ?? undefined));
    setReplyBody("");
    setPrivateNote(false);
    setZammadArticleType("note");
    setError(null);
    setSuccess(null);
  }, [open, ticket]);

  if (!ticket) return null;

  const saveMetadata = async () => {
    setError(null);
    setSuccess(null);
    setMetaLoading(true);
    try {
      const nextMeta: Record<string, unknown> = {
        ...(ticket.metadata && typeof ticket.metadata === "object" ? ticket.metadata : {}),
        machineMacIds: parseMacIds(macInput),
      };
      await axiosInstance.patch("/customer-service/helpdesk/tickets/metadata", {
        unifiedId: ticket.id,
        metadata: nextMeta,
      });
      setSuccess("Metadata saved.");
      onSaved();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to save metadata.";
      setError(msg);
    } finally {
      setMetaLoading(false);
    }
  };

  const sendReply = async () => {
    setError(null);
    setSuccess(null);
    setReplyLoading(true);
    try {
      await axiosInstance.post("/customer-service/helpdesk/tickets/reply", {
        unifiedId: ticket.id,
        content: replyBody.trim(),
        private: ticket.source === "chatwoot" ? privateNote : undefined,
        zammadArticleType: ticket.source === "zammad" ? zammadArticleType : undefined,
      });
      setReplyBody("");
      setSuccess("Reply sent.");
      onSaved();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to send reply.";
      setError(msg);
    } finally {
      setReplyLoading(false);
    }
  };

  const canReply = ticket.source === "zammad" || ticket.source === "chatwoot";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Ticket
        <IconButton aria-label="close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          ID
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5, wordBreak: "break-all" }}>
          {ticket.id}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Subject
        </Typography>
        <Typography variant="body1" sx={{ mb: 1.5 }}>
          {ticket.title}
        </Typography>
        {ticket.externalUrl && (
          <Button
            href={ticket.externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ mb: 2 }}
          >
            Open in {ticket.source === "zammad" ? "Zammad" : ticket.source === "chatwoot" ? "Chatwoot" : "external"}
          </Button>
        )}
        {ticket.helpdeskCustomerId && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onOpenCustomer(ticket.helpdeskCustomerId!)}
            >
              View customer profile
            </Button>
          </Box>
        )}
        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
          Machine MAC IDs (comma-separated)
        </Typography>
        <TextField
          value={macInput}
          onChange={(e) => setMacInput(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
          placeholder="mac_abc123, mac_def456"
        />
        <Box sx={{ mt: 1, mb: 2 }}>
          <Button variant="outlined" size="small" onClick={() => void saveMetadata()} disabled={metaLoading}>
            Save metadata
          </Button>
        </Box>
        {canReply && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Reply
            </Typography>
            {ticket.source === "zammad" && (
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel id="zammad-article-type">Article type</InputLabel>
                <Select
                  labelId="zammad-article-type"
                  label="Article type"
                  value={zammadArticleType}
                  onChange={(e) => setZammadArticleType(e.target.value as "note" | "email")}
                >
                  <MenuItem value="note">Note</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                </Select>
              </FormControl>
            )}
            {ticket.source === "chatwoot" && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={privateNote}
                    onChange={(e) => setPrivateNote(e.target.checked)}
                  />
                }
                label="Private note (agents only)"
                sx={{ mb: 1 }}
              />
            )}
            <TextField
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              fullWidth
              multiline
              minRows={3}
              size="small"
              placeholder="Your message…"
            />
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => void sendReply()}
                disabled={replyLoading || !replyBody.trim()}
              >
                Send reply
              </Button>
            </Box>
          </>
        )}
        {!canReply && (
          <Typography variant="body2" color="text.secondary">
            Replies are not available for this source from this screen.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
